import { Pressable, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { colors } from '@chillout/core'

interface RewardBalanceCardProps {
  name: string
  count: number
  delta: number
  earliestExpiry?: string | null
  language?: string
  selected?: boolean
  onPress?: () => void
  accessibilityLabel?: string
}

function formatExpiry(dateString: string, language: string): string {
  return new Date(dateString).toLocaleDateString(language, { day: 'numeric', month: 'short', year: 'numeric' })
}

export function RewardBalanceCard({
  name,
  count,
  delta,
  earliestExpiry,
  language,
  selected,
  onPress,
  accessibilityLabel,
}: RewardBalanceCardProps) {
  const { t } = useTranslation()
  const selectable = onPress !== undefined

  const inner = (
    <View
      style={selectable ? {
        borderWidth: selected ? 2 : 1,
        borderColor: selected ? colors.accent.DEFAULT : colors.border,
        backgroundColor: selected ? colors.accent.surface : 'transparent',
        borderRadius: 8,
        paddingHorizontal: 20,
        paddingVertical: 16,
      } : undefined}
      className={selectable ? undefined : 'rounded-lg border border-border px-5 py-4'}
    >
      <View className="flex-row items-center justify-between gap-4">
        <Text className="min-w-0 flex-1 text-base font-semibold text-primary" numberOfLines={1}>
          {name}
        </Text>
        <Text className="text-base font-semibold text-primary">×{count}</Text>
      </View>
      {earliestExpiry !== undefined && (
        <Text className="mt-0.5 text-xs font-normal text-muted" numberOfLines={1}>
          {earliestExpiry
            ? t('members.detail.availableRewards.expiresAt', { date: formatExpiry(earliestExpiry, language ?? 'en') })
            : '—'}
        </Text>
      )}
    </View>
  )

  return (
    <View>
      {delta !== 0 && (
        <View
          className={`absolute -top-3 -right-3 z-10 min-w-[28px] items-center rounded-full px-2.5 py-1 ${delta > 0 ? 'bg-success' : 'bg-accent'}`}
        >
          <Text className="text-sm font-bold text-inverse">
            {delta > 0 ? `+${delta}` : delta}
          </Text>
        </View>
      )}

      {selectable ? (
        <Pressable
          onPress={onPress}
          accessibilityRole="radio"
          accessibilityState={{ selected }}
          accessibilityLabel={accessibilityLabel}
        >
          {inner}
        </Pressable>
      ) : inner}
    </View>
  )
}